export enum ProgressOrderStatus {
    CREATING_DELIVERY_ORDER = 'CREATING_DELIVERY_ORDER',
    DELIVERY_ORDER_IS_INVALID = 'DELIVERY_ORDER_IS_INVALID',
    ERROR_WHEN_CREATING_DELIVERY_ORDER = 'ERROR_WHEN_CREATING_DELIVERY_ORDER',
    WAITING_FOR_SHIPMENT = 'WAITING_FOR_SHIPMENT',
    DELIVERY_EXPIRED = 'DELIVERY_EXPIRED',
    CANCELLED_BEFORE_DELIVERY_BY_SELLER = 'CANCELLED_BEFORE_DELIVERY_BY_SELLER',
    CANCELLED_BEFORE_DELIVERY_BY_CUSTOMER = 'CANCELLED_BEFORE_DELIVERY_BY_CUSTOMER',
    CANCELLED_IN_DELIVERY_BY_CUSTOMER = 'CANCELLED_IN_DELIVERY_BY_CUSTOMER',
    CANCELLED_AFTER_DELIVERED_BY_CUSTOMER = 'CANCELLED_AFTER_DELIVERED_BY_CUSTOMER',
    CANCELLED_BY_EXPIRED_PICKING = 'CANCELLED_BY_EXPIRED_PICKING',
    ORDER_IS_RETURNING_TO_SELLER = 'ORDER_IS_RETURNING_TO_SELLER',
    ORDER_RETURNED_TO_SELLER_POINT = 'ORDER_RETURNED_TO_SELLER_POINT',
    RETURNED_ORDER_WAS_PICKED_BY_SELLER = 'RETURNED_ORDER_WAS_PICKED_BY_SELLER',
    IN_DELIVERY = 'IN_DELIVERY',
    IN_DELIVERY_POINT = 'IN_DELIVERY_POINT',
    RECEIVED = 'RECEIVED',
    REQUIRED_SENDER_INFORMATION = 'REQUIRED_SENDER_INFORMATION',
}

export type UserOrderRelationType = 'buying' | 'selling';

export interface LatestUserOrderInternal {
    id?: string | null;
    orderId?: string | null;
    isArchive?: boolean | null;
    status: ProgressOrderStatus;
    creationDate: string;
    brand: string;
    model: string;
    totalPrice: number;
    serialNumber?: string | number | null;
    orderSerialNumber?: string | number | null;
}

export interface LatestUserOrdersInternalResponse {
    orders: LatestUserOrderInternal[];
    hasNextOrders: boolean;
}

export enum PromoCodeType {
    Percent = 'Percent',
    FixSum = 'FixSum',
}

export enum DirectDeliveryState {
    SUCCESS = 'SUCCESS',
    IN_PROGRESS = 'IN_PROGRESS',
    CANCELLED_BY_OUR_SYSTEM = 'CANCELLED_BY_OUR_SYSTEM',
    MANUAL_CANCELLED_BEFORE_PROGRESS = 'MANUAL_CANCELLED_BEFORE_PROGRESS',
    MANUAL_CANCELLED_IN_PROGRESS = 'MANUAL_CANCELLED_IN_PROGRESS',
    WAITING_RETURN_ORDER_INFO = 'WAITING_RETURN_ORDER_INFO',
    DONE = 'DONE',
    RETURN = 'RETURN',
}

export enum ReturnDeliveryState {
    SUCCESS = 'SUCCESS',
    IN_PROGRESS = 'IN_PROGRESS',
    WAITING_PICKING = 'WAITING_PICKING',
    DONE = 'DONE',
}

export enum OrderPaymentErrorState {
    PAYOUT_CARD_NOT_FOUND = 'PAYOUT_CARD_NOT_FOUND',
    PAYOUT_CARD_INACTIVE = 'PAYOUT_CARD_INACTIVE',
}

export enum OrderPaymentState {
    CREATED = 'CREATED',
    REVERSED = 'REVERSED',
    REFUND = 'REFUND',
    REFUND_ERROR = 'REFUND_ERROR',
    FINISHED = 'FINISHED',
    PENDING_PAYOUT = 'PENDING_PAYOUT',
    PAID = 'PAID',
    PAID_ERROR = 'PAID_ERROR',
}

export interface OrderPaymentInfoInternalResponse {
    id: string;
    ypmnId: string;
    state: OrderPaymentState | string;
    amountSeller: number;
    orderId: string;
    adTitleInfo: string;
    payoutBlocked: boolean;
    finishedAt?: string | null;
    paidAt?: string | null;
    payoutErrorState?: OrderPaymentErrorState | string | null;
    payoutMethod?: string | null;
}

export type SellerReceiptEligibility =
    | 'ELIGIBLE'
    | 'ORDER_NOT_RECEIVED'
    | 'PAYOUT_NOT_PAID'
    | 'PAYMENT_NOT_FOUND';

export type SellerReceiptStatus = 'PENDING' | 'FISCALIZED' | string;

export interface SellerReceipt {
    id: string;
    orderPaymentId: string;
    orderId: string;
    status: SellerReceiptStatus;
    providerReceiptId: string;
    amount: string;
    regTime?: string | null;
    fiscalSign?: number | null;
    fiscalNum?: number | null;
    receiptUrl?: string | null;
    createdAt: string;
    updatedAt: string;
    fiscalizedAt?: string | null;
}

export interface SellerReceiptContext {
    eligibility: SellerReceiptEligibility;
    receipt: SellerReceipt | null;
}

export type SellerReceiptAttemptOutcome = 'IN_PROGRESS' | 'SUCCESS' | 'ERROR';

export interface SellerReceiptCreationAuditItem {
    id: string;
    orderId: string;
    paymentId: string;
    outcome: SellerReceiptAttemptOutcome;
    httpStatus: number | null;
    receiptStatus: SellerReceiptStatus | null;
    errorId: string | null;
    errorMessage: string | null;
    initiatedAt: string | null;
    completedAt: string | null;
    actor: {
        id: string;
        login: string;
        fullName: string;
        role: string;
    };
}

export interface UserInfo {
    id: string;
    name: string;
    avatarUrl: string | null;
}

export interface UsersInfoInternal {
    seller: UserInfo;
    customer: UserInfo;
}

export interface AdInfoInternal {
    adId: string;
    brand: string;
    model: string;
}

export interface HistoryStatusInternal {
    status: ProgressOrderStatus;
    date: string;
}

export interface PromoCodeInfoInternal {
    code: string;
    type: PromoCodeType;
    amount: number;
}

export interface DiscountInfoInternal {
    discount: number;
    priceWithoutDiscount: number;
    info: PromoCodeInfoInternal;
}

export interface FeeInfoInternal {
    percent: number;
    amount: number;
}

export interface LoyaltyInfoInternal {
    earn: number | null;
    used: number | null;
}

export interface PriceInfoInternal {
    adPrice: number;
    originalPrice: number;
    totalPrice: number;
    deliveryPrice: number;
    fee: FeeInfoInternal;
    loyalty?: LoyaltyInfoInternal | null;
    discount: DiscountInfoInternal | null;
}

export interface ReceiverCredentialsInternal {
    fullName: string;
    phoneNumber: string;
    email: string;
}

export interface DeliveryInfoInternal {
    provider: string;
    from: string;
    to: string;
}

export interface HistoryItemInternal {
    orderStatus: string;
    date: string;
}

export interface PvzInfoInternal {
    code: string;
    fullAddress: string;
}

export interface ProviderDirectDeliveryInfoInternal {
    providerDeliveryOrderId: string;
    state: DirectDeliveryState;
    history: HistoryItemInternal[];
    trackNumber: string;
    approximateDeliveryDate: string | null;
    returnDeliveryOrderId: string | null;
}

export interface ProviderReturnDeliveryInfoInternal {
    id: string;
    providerDeliveryOrderId: string;
    state: ReturnDeliveryState;
    history: HistoryItemInternal[];
    trackNumber: string;
    approximateDeliveryDate: string | null;
    pvzInfo: PvzInfoInternal | null;
}

export interface ProgressOrderInfoInternalResponse {
    status: ProgressOrderStatus;
    orderId: string;
    serialNumber: string;
    adInfo: AdInfoInternal;
    usersInfo: UsersInfoInternal;
    statusesHistory: HistoryStatusInternal[];
    priceInfo: PriceInfoInternal;
    creationDate: string;
    lastUpdate: string;
    receiverInfo: ReceiverCredentialsInternal;
    deliveryInfo: DeliveryInfoInternal;
    providerDirectDeliveryInfo: ProviderDirectDeliveryInfoInternal | null;
    providerReturnDeliveryInfo: ProviderReturnDeliveryInfoInternal | null;
}
